
// ── coordinates ───────────────────────────────────────────────────────────
export const COORD_BOUNDS = {
  minLat: -90, maxLat: 90, minLng: -180, maxLng: 180,
};

/** A coordinate that could exist anywhere on earth. */
export const isValidCoordinate = (lat, lng) => {
  const la = parseFloat(lat), lo = parseFloat(lng);
  if (Number.isNaN(la) || Number.isNaN(lo)) return false;
  return la >= COORD_BOUNDS.minLat && la <= COORD_BOUNDS.maxLat &&
         lo >= COORD_BOUNDS.minLng && lo <= COORD_BOUNDS.maxLng;
};

// ── GPS jamming ───────────────────────────────────────────────────────────
// This check used to be `isInIsrael`, a fixed bounding box, and it was easy to
// read as a jurisdiction gate. It never was one: both call sites only ever
// showed a warning, and the warning's own text says "possible jamming. Saved
// as-is". It was built because GPS jamming is common here and a device will
// report a confident fix hundreds of kilometres away without any error.
//
// A fixed box cannot survive going global -- every genuine foreign specimen
// would trip it. What actually indicates a spoofed fix is a coordinate
// implausibly far from where this device has recently been, so that is what is
// measured now. With no reference point yet, nothing is claimed.

// Jamming displaces a fix by hundreds of kilometres, not tens. Set well above
// any plausible day's travel so a real trip to another region is not flagged.
export const JAMMING_DISTANCE_KM = 500;

const REFERENCE_KEY = 'mb_gps_reference';
const EARTH_RADIUS_KM = 6371;
const toRad = (deg) => (deg * Math.PI) / 180;

/** Great-circle distance in kilometres. */
export function distanceKm(lat1, lng1, lat2, lng2) {
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
}

// ── navigating to a target ────────────────────────────────────────────────
// Field navigation (the national map's target and the camera's mini-map)
// says how far and which way a chosen observation lies from the phone's own
// fix. Pure helpers, so the overlay and the banner cannot disagree.

/**
 * Initial great-circle bearing from the first point to the second, in degrees
 * clockwise from true north, 0 ≤ b < 360. Two coincident points bear 0.
 */
export function bearingDeg(lat1, lng1, lat2, lng2) {
  const p1 = toRad(lat1);
  const p2 = toRad(lat2);
  const dLng = toRad(lng2 - lng1);
  const y = Math.sin(dLng) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dLng);
  const deg = (Math.atan2(y, x) * 180) / Math.PI;
  const bearing = (deg + 360) % 360;
  // -0 and 360 are both north.
  return bearing >= 360 || Object.is(bearing, -0) ? 0 : bearing;
}

//: The eight compass points, as the keys of common:compass.*.
export const COMPASS_POINTS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

/** The compass point a bearing falls in: each point owns 45°, centred on it. */
export function compassPoint(bearing) {
  const b = ((Number(bearing) % 360) + 360) % 360;
  return COMPASS_POINTS[Math.round(b / 45) % 8];
}

//: Under this many metres the person is at the target, not on the way to it:
//: a phone's fix is rarely better than a few metres.
export const ARRIVED_M = 10;

/**
 * A distance in kilometres as the digits and the unit it is shown in: metres
 * under a kilometre (whole), else kilometres to one decimal. The text itself
 * is `t(`common:distance.${unit}`, { n: value })`, so Hebrew puts the unit
 * where it goes and the digits stay digits (see distanceLabel).
 */
export function formatDistance(km) {
  const k = Number(km);
  if (!Number.isFinite(k) || k < 0) return null;
  if (k < 1) return { value: String(Math.round(k * 1000)), unit: 'm' };
  return { value: k.toFixed(1), unit: 'km' };
}

/** formatDistance, said through the catalogue (common:distance.m / .km). */
export function distanceLabel(km, t) {
  const parts = formatDistance(km);
  return parts ? t(`common:distance.${parts.unit}`, { n: parts.value }) : '';
}

/** A bearing said through the catalogue: the compass point and the degrees. */
export function bearingLabel(bearing, t) {
  if (!Number.isFinite(Number(bearing))) return '';
  return t('common:bearing', { point: t(`common:compass.${compassPoint(bearing)}`), deg: Math.round(Number(bearing)) % 360 });
}

/**
 * Where a target lies from a fix: {km, bearing, arrived}, or null without
 * both points. `arrived` is within ARRIVED_M.
 */
export function towards(fix, target) {
  if (!fix || !target) return null;
  const [a, b, c, d] = [fix.lat, fix.lng, target.lat, target.lng].map(Number);
  if (![a, b, c, d].every(Number.isFinite)) return null;
  const km = distanceKm(a, b, c, d);
  return { km, bearing: bearingDeg(a, b, c, d), arrived: km * 1000 < ARRIVED_M };
}

/**
 * Remember where this device actually was. Only ever called with a fix the
 * device itself produced -- a pin dropped on a map is a statement about the
 * specimen, not about where the operator is standing, and using it as a
 * reference would let one deliberate faraway pin poison every later check.
 */
export function rememberReferencePoint(lat, lng) {
  if (!isValidCoordinate(lat, lng)) return;
  try {
    localStorage.setItem(REFERENCE_KEY, JSON.stringify({
      lat: parseFloat(lat), lng: parseFloat(lng), at: Date.now(),
    }));
  } catch (e) {
    // Private browsing, cleared site data, storage disabled. The check simply
    // has no reference and stays silent, which is the correct degraded state.
  }
}

export function getReferencePoint() {
  try {
    const raw = localStorage.getItem(REFERENCE_KEY);
    if (!raw) return null;
    const point = JSON.parse(raw);
    return isValidCoordinate(point.lat, point.lng) ? point : null;
  } catch (e) {
    return null;
  }
}

/**
 * True when a fix looks jammed. False when it looks fine OR when there is no
 * basis to judge -- the two are deliberately not distinguished by the return
 * value, because a warning shown on no evidence is worse than no warning.
 */
export function looksJammed(lat, lng, reference = getReferencePoint()) {
  if (!isValidCoordinate(lat, lng)) return false;
  if (!reference) return false;
  return distanceKm(reference.lat, reference.lng,
                    parseFloat(lat), parseFloat(lng)) > JAMMING_DISTANCE_KM;
}

// ── Leaflet ───────────────────────────────────────────────────────────────
// STILL BUNDLED, not injected from unpkg at run time. The CDN version could
// not work offline at all -- which defeats the point of a field application
// -- and a shipped app that downloads and executes a remote script is an App
// Store §2.5.2 problem on top of that. `import('leaflet')` is a different
// thing entirely: the chunk is built into this application, served from its
// own origin, precached by the service worker, and on a phone it is a file
// already inside the installed app. Nothing is fetched from anyone else.
//
// Why it moved (Task 4.3b): leaflet is 150 kB and this module's OTHER
// exports -- isValidCoordinate, looksJammed, captureDeviceGps -- are pure
// helpers with nothing to do with maps. A top-level import meant the
// collection wizard and the detail view dragged the whole mapping library in
// to ask whether a latitude is a number.
//
// The signature is unchanged: it still returns a Promise, and every call site
// already awaits it and then reads window.L, so nothing else needed touching.
export function loadLeaflet() {
  if (typeof window !== 'undefined' && window.L) return Promise.resolve(window.L);
  return Promise.all([
    import('leaflet'),
    // The stylesheet is part of the map: without it the tiles stack instead
    // of tiling. It rides the same chunk, so it cannot arrive separately.
    import('leaflet/dist/leaflet.css'),
  ]).then(([module]) => {
    const L = module.default || module;
    if (typeof window !== 'undefined') window.L = L;
    return L;
  });
}

// Wraps the browser Geolocation API in a Promise. Rejects with an Error
// (never the raw GeolocationPositionError) so callers can .catch() uniformly
// whether geolocation is unsupported or the user denies/it times out.
export function captureDeviceGps() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('geolocation-unsupported')); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(6);
        const lng = pos.coords.longitude.toFixed(6);
        // The device told us where it is; that is exactly what a later
        // plausibility check needs to compare against.
        rememberReferencePoint(lat, lng);
        resolve({ lat, lng });
      },
      () => reject(new Error('geolocation-failed')),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
}
